/**
 * Client-side runtime feature flags.
 *
 * REACT_APP_* variables are inlined at build time by the bundler, so these are
 * evaluated against whatever value was present when the frontend was built.
 *
 * All flags fail closed: anything other than the exact string 'true' is treated as
 * disabled, so an unset, empty or malformed variable never accidentally enables a feature.
 */

export const isFlagEnabled = value =>
  String(value)
    .trim()
    .toLowerCase() === 'true';

/**
 * Dock.io/Truvera W3C credential issuance and its proof-presentation flow.
 * Mirrors DOCK_CREDENTIALS_ENABLED on the server.
 */
export const isDockCredentialsEnabled = () =>
  isFlagEnabled(process.env.REACT_APP_DOCK_CREDENTIALS_ENABLED);

/**
 * Whether an EAD's category must be read from the card.
 * Mirrors EAD_CATEGORY_CHECK_ENABLED on the server.
 */
export const isEadCategoryCheckEnabled = () =>
  isFlagEnabled(process.env.REACT_APP_EAD_CATEGORY_CHECK_ENABLED);

/**
 * Whether the Certificates of Naturalization and Citizenship and DS-2019 are offered as work
 * documents. Mirrors ADDITIONAL_WORK_DOCUMENTS_ENABLED on the server.
 */
export const isAdditionalWorkDocumentsEnabled = () =>
  isFlagEnabled(process.env.REACT_APP_ADDITIONAL_WORK_DOCUMENTS_ENABLED);

/**
 * HTTP status the server uses for a switched-off feature. Deliberately not 404, which
 * would be indistinguishable from a routing bug.
 */
export const FEATURE_DISABLED_STATUS = 503;

/**
 * True when an API error represents "this feature is switched off" rather than a genuine
 * failure. Used as a safety net for the case where the frontend build and the server have
 * drifted out of sync on the flag — the frontend check should normally short-circuit first.
 */
export const isFeatureDisabledError = error => {
  if (!error) {
    return false;
  }

  if (error.status === FEATURE_DISABLED_STATUS) {
    return true;
  }

  // getProofRequest surfaces the raw response body as the message, so fall back to
  // sniffing the marker the server embeds in its disabled payload.
  return typeof error.message === 'string' && error.message.includes('FEATURE_DISABLED');
};
